// ==================== Pipeline Run Types ====================

import { MediaOutputSpec } from './media-spec';
// Type-only: the discussion API already declares these shapes, and the run
// payload carries the very same objects. Importing erases at compile time, so
// no runtime dependency is created in either direction.
import type { DiscussionOption, DiscussionTurn } from '../discussion';
// Type-only for the same reason: settings.ts imports this module for its review types.
import type { GenerationSettings } from '../settings';
// Type-only: director.ts imports this module's statuses back.
import type { DirectorState } from './director';

export type PipelineRunStatus = 
  | 'pending' 
  | 'running' 
  | 'paused' 
  | 'completed' 
  | 'failed' 
  | 'cancelled'
  // A director run is parked until the director (the chat operator) acts.
  | 'awaiting_director';

export type CheckpointStatus = 
  | 'pending' 
  | 'running' 
  | 'completed' 
  | 'failed' 
  | 'awaiting_asset'
  | 'awaiting_confirm'
  | 'skipped';

export type CheckpointType =
  | 'prompt'
  | 'distributor'
  | 'connector'
  | 'generator'
  | 'upload'
  | 'sequencer'
  // Runs one contentgen-sourcing job (Go CheckpointTypeTool, types/status.go:39).
  | 'tool'
  // Runs an agent: a model that looks, tries and decides through tools and
  // finishes with a contract-valid JSON result (Go CheckpointTypeAgent, types/agent.go).
  | 'agent';

export interface CostProviderTotal {
  total_cost?: number;
  run_cost?: number;
  per_clip_cost?: number;
  clip_count?: number;
  currency?: string;
  estimated?: boolean;
  [key: string]: unknown;
}

export interface CostSummary {
  estimated?: boolean;
  currency?: string;
  total_cost?: number;
  providers?: Record<string, CostProviderTotal>;
  per_run?: Record<string, CostProviderTotal>;
  per_clip?: Record<string, CostProviderTotal>;
  clips?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface MediaAttachment {
  id: string;
  media_id?: string;
  role?: string;
  scene_id?: string;
  frame_order?: number;
  type: string;
  url: string;
  mime_type: string;
  name: string;
  filename?: string;
  created_at: string;
  source?: string;
  size_bytes?: number;
  size?: number;
  metadata?: Record<string, unknown>;
  checkpoint_id?: string;
  checkpoint_index?: number;
  source_checkpoint_id?: string;
  source_run_id?: string;
  generated_from_checkpoint_id?: string;
  generated_from_field?: string;
}

export interface PipelineInputAttachment {
  type: string;
  role?: string;
  scene_id?: string;
  frame_order?: number;
  source?: string;
  state?: string;
  url?: string;
  name?: string;
  filename?: string;
  mime_type?: string;
  size_bytes?: number;
  size?: number;
  media_id?: string;
  metadata?: Record<string, unknown>;
  checkpoint_id?: string;
  checkpoint_index?: number;
  source_checkpoint_id?: string;
  source_run_id?: string;
  generated_from_checkpoint_id?: string;
  generated_from_field?: string;
}

/**
 * A distributor child generation that failed while the rest of the fan-out
 * succeeded. Surfaced on the paused checkpoint so the review UI can show the
 * failed slot with a legible reason and offer a regenerate.
 */
export interface ChildFailure {
  child_run_id: string;
  /** The child's distributor item (scene/angle plan JSON) — labels the slot. */
  input?: string;
  /** Human-legible failure message (e.g. "Rejected by provider safety filter"). */
  reason: string;
  error_code?: number;
  /** What kind of failure it was (the server's word, e.g. "content_filtered"). */
  kind?: string;
  /** The provider that refused or failed it. */
  provider?: string;
  /** The failed clip's scene id, so the failure finds its card without parsing `input`. */
  scene_id?: string;
}

/** One judged criterion inside a verdict. */
export interface ReviewFinding {
  criterion: string;
  /** PASS | FAIL | UNCERTAIN — UNCERTAIN counts as a pass. */
  verdict: string;
  evidence?: string;
  /**
   * A DEFINITE criterion: one the engine's own gate rules on rather than
   * trusting the judge's overall verdict. A required finding that is not a PASS
   * fails the attempt whatever the judge said — including over a confirm-tier
   * PASS (`review.go`, "required check overrides a confirm-tier pass").
   * Stamped by the cascade (`review_checks.go:326`); absent on older verdicts.
   */
  required?: boolean;
  /**
   * An ADVISORY criterion: a prediction about something that does not exist
   * yet (the future video) or a taste note. Its FAIL is shown and may route a
   * fix, but never fails the frame — a verdict whose only FAILs are advisory
   * is a pass, with them listed in `metadata.advisory_failures`.
   */
  advisory?: boolean;
}

/**
 * One AI review pass over a checkpoint's output. Stored append-only on the
 * checkpoint result, newest LAST; a checkpoint with no review has none.
 */
export interface ReviewVerdict {
  /** Present on every verdict recorded after corrections shipped; absent on old runs. */
  id?: string;
  attempt: number;
  /** check | confirm | pick — which tier produced this verdict. */
  tier: string;
  pass: boolean;
  confidence: number;
  /** blocking | tolerable | none. */
  severity: string;
  /**
   * `unreviewed` when the reviewer could not give a verdict at all — an
   * outage, never a ruling on the candidate, which was kept. Absent on every
   * verdict the judge ruled on, and on every verdict recorded before outages
   * were told apart (those carry `metadata.review_unresolved` and `pass:false`
   * and still read as a failed review).
   */
  outcome?: 'unreviewed' | string;
  /** Why an unreviewed verdict has no ruling: transient | config | credits. */
  failure_kind?: 'transient' | 'config' | 'credits' | string;
  findings?: ReviewFinding[];
  critique?: string;
  /** The rewritten generation prompt a confirmed fail proposes. */
  adjusted_prompt?: string;
  /**
   * Extension channel (clip trim points, pick winner, ...). The reviewer's own
   * stamps include `review_unresolved` (the engine's reason for an outage),
   * `judge_retry` (`{first_trace_id, first_outcome, first_finish}` — the call
   * was retried once), `advisory_failures` (the advisory criteria a pass
   * noted) and `pick_undecided`.
   */
  metadata?: Record<string, unknown>;
  provider?: string;
  model?: string;
  /** Which frame of a two-frame generator this verdict judged: "closing" for
   *  the scene's closing frame. Absent means the opening frame — including on
   *  every verdict recorded before closing frames existed. */
  frame?: string;
  /** Attachment id judged, or "" for a text candidate. */
  candidate_id?: string;
  created_at: string;
}

/** One image (candidate frame or reference) shown to the reviewer for a trace. */
export interface ReviewTraceImage {
  label: string;
  media_id?: string;
  url?: string;
}

/**
 * One raw reviewer call, independent of the verdict it produced. A trace read
 * from the store carries the full `system_prompt` / `raw_output`; a trace
 * read off the live run (before the store write lands) carries only the
 * 2 KB-capped `prompt_head` / `output_head`.
 */
export interface ReviewTrace {
  id: string;
  verdict_id?: string;
  run_id?: string;
  checkpoint_id: string;
  checkpoint_index: number;
  attempt: number;
  tier: string;
  frame?: string;
  candidate_id?: string;
  provider?: string;
  model?: string;
  prompt_head?: string;
  system_prompt?: string;
  prompt_chars: number;
  prompt_hash?: string;
  images?: ReviewTraceImage[];
  latency_ms: number;
  outcome:
    | 'pass'
    | 'fail'
    | 'unparseable'
    | 'error'
    | 'truncated'
    | 'not_called'
    | string;
  /**
   * Why the reply stopped, only on one that did not finish: the provider's own
   * word (`MAX_TOKENS`, `SAFETY`, …), or `none` when the stream ended without
   * one. Read off the run's copy of the trace, so a row evicted from the run's
   * cap never carries it.
   */
  finish_reason?: 'MAX_TOKENS' | 'none' | '' | string;
  output_head?: string;
  raw_output?: string;
  created_at: string;
}

/**
 * How far a lesson reaches. The store's enum verbatim — `this_checkpoint` binds
 * it to the checkpoint it was recorded on, `this_pipeline` to the template,
 * `this_model` to one generator model wherever it is used, `all_pipelines` to
 * every template the user owns.
 */
export type ReviewCorrectionScope =
  | 'this_checkpoint'
  | 'this_pipeline'
  | 'this_model'
  | 'all_pipelines';

/** A human's override of a reviewer verdict — logged for judge-kit training. */
export interface ReviewCorrection {
  id: string;
  verdict_id: string;
  label: 'false_pass' | 'false_fail' | 'correct';
  reason: string;
  /**
   * The self-contained RULE distilled from `reason` — what the judge is
   * actually shown. Empty on a correction saved without distilling, where the
   * backend still renders the raw reason.
   */
  lesson?: string;
  scope: ReviewCorrectionScope | string;
  /**
   * The generator model a `this_model` lesson is about, as
   * `<provider>/<model>` (e.g. `runware/google:4@1`). Derived by the BACKEND
   * from the corrected checkpoint's generator; empty on every other scope.
   */
  model_id?: string;
  /** The lesson this one reinforces; the store bumps that row's `weight`. */
  reinforces_id?: string;
  /** How many times this lesson has been recorded. 1 (or absent) = once. */
  weight?: number;
  created_at: string;
}

/**
 * What the judge made of one correction note: a rule stated so it survives
 * without the frame that prompted it, the reach it should have, and — when the
 * same rule already exists — the lesson it reinforces rather than duplicates.
 *
 * `suggested_action` is the one repair the distil call may propose; the
 * frontend performs it through the existing endpoint, never a new one.
 */
export interface ReviewLessonDistillation {
  lesson: string;
  scope: ReviewCorrectionScope;
  /** '' when this is a new lesson rather than a repeat of an existing one. */
  reinforces_id: string;
  /** The raw note it was distilled from, echoed back. */
  reason: string;
  suggested_action: '' | 'drop_closing_frame' | string;
}

/**
 * Cost projection a sequencer publishes once its plan is validated and BEFORE
 * any child spawns, so the confirm gate can say what the run is about to cost.
 * Mirrors Go pipeline.StepProjection.
 */
export interface StepProjection {
  step_count: number;
  /** Credits for the whole sequence (per_step_estimate × step_count). */
  estimated_credits: number;
  per_step_estimate: number;
  provider?: string;
  model?: string;
}

/**
 * The conversation held at a discussion checkpoint, as stored on its result.
 *
 * The run list returns `DiscussionState` whole — no projection — so the entire
 * conversation rides along with every run payload. `options` is declared because
 * the Series page needs it: a checkpoint parks with `phase: diverge` and an
 * EMPTY option list before its first turn has run, so "parked" on its own is not
 * the same question as "parked with something to answer".
 */
export interface CheckpointDiscussionState {
  phase: 'diverge' | 'focus' | 'ready';
  chosen_id?: string;
  turn_count?: number;
  /** The concepts on the table. Absent or empty = the conversation has not started. */
  options?: DiscussionOption[];
  turns?: DiscussionTurn[];
  /** Set once a brief has been approved; the checkpoint then runs for real. */
  finalized?: string;
}

/**
 * The pre-generation review of a whole distributor/sequencer plan, recorded
 * BEFORE any scene child spends. `unresolved` parks the run and refuses a plain
 * Continue; the plan review's own verdicts ride on `verdicts` with
 * `frame === 'plan'`.
 */
export interface PlanPreflightState {
  /**
   * unresolved | passed | skipped | overridden (user accepted) |
   * repair_requested (a fix is running) | unreviewed (the reviewer was
   * unavailable, so the plan ran without a review).
   */
  status:
    | 'unresolved'
    | 'passed'
    | 'skipped'
    | 'overridden'
    | 'repair_requested'
    | 'unreviewed'
    | string;
  reason?: string;
  plan_sha256?: string;
  checked_at?: string;
  duration_constraints?: string;
  /** repair_requested only: the notes the AI rewrite was asked to follow. */
  repair_notes?: string[];
}

/**
 * One deterministic lint on a distributor/sequencer plan, stored on the step so
 * a paused plan shows what the checks found. Not a reviewer verdict.
 */
export interface PlanWarning {
  /** timing | cast | prompt_spec | image_timestamp */
  code: string;
  /** The step the warning is about; absent for the plan as a whole. */
  step?: number;
  message: string;
}

/** One line of an agent run as the studio shows it (Go agentruntime.LogEntry). */
export interface AgentLogEntry {
  turn: number;
  kind: 'model' | 'tool' | 'finish' | 'limit' | 'error' | 'resumed';
  tool?: string;
  args?: string;
  text?: string;
  images?: string[];
  media?: string[];
  cost_usd?: number;
  error?: boolean;
  at: string;
}

/** An agent checkpoint's saved run (Go agentruntime.State, the fields the UI reads). */
export interface AgentRunState {
  status: 'running' | 'finished' | 'failed' | 'exhausted' | 'stopped' | 'cancelled';
  provider: string;
  model: string;
  turns: number;
  cost_usd: number;
  log?: AgentLogEntry[];
  error?: string;
}

export interface CheckpointResult {
  discussion?: CheckpointDiscussionState;
  checkpoint_id: string;
  status: CheckpointStatus;
  input: string;
  output: string;
  error?: string;
  error_code?: number;
  /** What kind of failure it was, when the engine could tell (Go CheckpointResult.FailureKind). */
  failure_kind?: string;
  /** The provider behind the failure (Go CheckpointResult.FailureProvider). */
  failure_provider?: string;
  attachments?: MediaAttachment[];
  /** Pinned attachment id — which generation of this checkpoint downstream
   *  steps consume. Empty/absent means "the most recent generation wins". */
  selected_attachment_id?: string;
  /** The same pin for the scene's CLOSING frame, when the generator produced
   *  one. It has its own slot because the two frames do not compete: picking a
   *  closing frame must never displace the chosen opening frame. */
  selected_closing_attachment_id?: string;
  failures?: ChildFailure[];
  /** AI review verdicts for this checkpoint, append-only, newest LAST. */
  verdicts?: ReviewVerdict[];
  /** Sequencer only: the validated plan's step count + credit estimate,
   *  published at the pre-spawn confirm gate. */
  projection?: StepProjection;
  /** Distributor/sequencer only: the pre-generation plan review's state. */
  plan_preflight?: PlanPreflightState;
  /** Distributor/sequencer only: the plan's lint warnings, stored on the step. */
  plan_warnings?: PlanWarning[];
  /** A user-requested park on this checkpoint. Deliberately not `error`:
   *  nothing failed, so it must never render as a fault. */
  pause?: PauseMark;
  /** Tool checkpoints only: the sourcing service's remarks (Go CheckpointResult.Notes). */
  notes?: string[];
  /** Agent checkpoints only: the agent's saved run (turns, log, cost). */
  agent?: AgentRunState;
  /** output_contract checkpoints only: the violations left after the engine's one
   *  repair call (Go CheckpointResult.ContractViolations). */
  contract_violations?: string[];
  started_at?: string;
  completed_at?: string;
  regenerate_count: number;
  child_pipeline_ids?: string[];
}

/**
 * The engine's record of a user-requested park: why the run stopped, plus —
 * for a fan-out stopped between children — how far it got.
 */
export interface PauseMark {
  note: string;
  /** Sequencer only: the sequence stopped before step steps_spawned+1. */
  steps_spawned?: number;
  steps_planned?: number;
  at: string;
}

export const ERR_INSUFFICIENT_CREDITS = 900;
export const ERR_PROVIDER_UNAVAILABLE = 901;
export const ERR_RATE_LIMITED = 902;
export const ERR_GENERATION_FAILED = 903;
export const ERR_CONTENT_REJECTED = 904;
/** An agent step reached its `limits.max_model_cost_usd`. Pauses the run (like
 *  900) instead of failing it: raise the cap, then Continue resumes. */
export const ERR_AGENT_MODEL_CAP = 905;
/** The run (or a shot of its fan-out) would pass the spending ceiling the user
 *  approved. Pauses it instead of spending: approving more resumes it. */
export const ERR_RUN_CEILING = 906;
/** A provider's own account is out of credit (not ours, which is 900). Pauses the
 *  run: the user tops the account up at the provider, then continues. */
export const ERR_PROVIDER_OUT_OF_CREDIT = 907;

/** Error codes whose hint tells the user to adjust a prompt. A checkpoint
 *  without one (a tool) drops these hints but keeps the credit/infra ones. */
export const isPromptErrorCode = (code: number | undefined): boolean =>
  code === ERR_GENERATION_FAILED || code === ERR_CONTENT_REJECTED;

export const getErrorCodeMessage = (code: number | undefined): string | null => {
  switch (code) {
    case ERR_INSUFFICIENT_CREDITS:
      return 'Insufficient credits — top up and retry.';
    case ERR_PROVIDER_UNAVAILABLE:
      return 'Provider temporarily unavailable — try again.';
    case ERR_RATE_LIMITED:
      return 'Rate limited — wait a moment and retry.';
    case ERR_GENERATION_FAILED:
      return 'Generation failed — you can retry or adjust the prompt.';
    case ERR_CONTENT_REJECTED:
      return 'Rejected by provider safety filter — adjust the prompt and regenerate.';
    case ERR_AGENT_MODEL_CAP:
      return 'This agent step reached its model cap — raise the cap, then continue; it resumes without repeating its paid turns.';
    case ERR_RUN_CEILING:
      return 'This run reached the spending you approved — approve more spending to continue; it resumes where it paused.';
    case ERR_PROVIDER_OUT_OF_CREDIT:
      return 'A provider account is out of credit. Top it up there, then continue.';
    default:
      return null;
  }
};

/**
 * One checkpoint's REWIRE: which reference images its generator must stop being
 * fed, and which it must be fed instead. Stored per RUN (children carry their
 * own), applied fresh on every generation.
 *
 * `drop` holds attachment ids / media ids (or a `checkpoint:…` spec); `add`
 * holds `checkpoint:<id>` or `checkpoint:<id>:<attachment_id>` specs — the same
 * vocabulary `required_assets` speaks, which is why an override still means
 * something after the run has moved on.
 *
 * `verdict_attempt` 0 means the USER made this rewire; a positive value means
 * the reviewer made it at that attempt. Mirrors Go pipeline.AttachmentOverride.
 */
export interface AttachmentOverride {
  drop?: string[];
  add?: string[];
  reason?: string;
  verdict_attempt?: number;
}

/**
 * Where a cast member came from:
 *  - `checkpoint` — a character reference checkpoint produced the picture;
 *  - `upload` — it came in as an uploaded run asset;
 *  - `plan` — the planner named the character (it may have no picture yet);
 *  - `user` — a human added it, or edited one of the above (which locks it);
 *  - `series` / `series:<character id>` — the show's cast sheet seeded it onto
 *    the episode's run before its first checkpoint ran (always `locked`). The
 *    id-suffixed form is what an episode start actually writes, pointing back
 *    at the cast-sheet row; the panel renders either as the plain label
 *    "series" (see `castSourceDisplay`).
 */
export type CastSource = 'checkpoint' | 'upload' | 'plan' | 'user' | 'series' | `series:${string}`;

/**
 * One character the run keeps a picture and a description of, so every scene —
 * and every judge — means the same person by the name.
 *
 * Run-level and ROOT-only: a scene's child run never carries a cast, it reads
 * the one on the root run of its thread.
 */
export interface CastMember {
  name: string;
  /** Other names the story (or the planner) calls this character by. */
  aliases?: string[];
  /** The short, literal traits that identify them ("red raincoat"). */
  anchors?: string[];
  media_id?: string;
  url?: string;
  source?: CastSource;
  /** User-edited: the engine never overwrites a locked member from a plan. */
  locked?: boolean;
  updated_at?: string;
}

export interface PipelineRun {
  id: string;
  pipeline_template_id: string;
  parent_run_id?: string;
  initial_input: string;
  current_checkpoint: number;
  status: PipelineRunStatus;
  /**
   * The user asked the run to stop at its next checkpoint boundary. It is NOT
   * a status: the run keeps reporting `running` (and its in-flight step keeps
   * going) until the engine reaches a boundary it can park on, which is the
   * window the UI labels "pausing after the current step". Cleared by Continue.
   */
  pause_requested?: boolean;
  results: CheckpointResult[];
  /** Per-checkpoint reference rewires, keyed by checkpoint id. */
  attachment_overrides?: Record<string, AttachmentOverride>;
  /**
   * Checkpoints whose generated closing frame is switched OFF, keyed by
   * checkpoint id. `by` says who flipped it — the reviewer's entries carry the
   * verdict's reasoning and render as the amber "dropped by reviewer" chip.
   * Absent on every run that never disabled one.
   */
  closing_frame_disabled?: Record<
    string,
    { by: 'user' | 'reviewer'; reason?: string; at?: string }
  >;
  auto_mode: boolean;
  /**
   * A director run carries its own graph instead of a saved pipeline: the
   * checkpoints the director has added so far. Read-only here; every mutating
   * run route answers 409 `director_run` for it (except cancel).
   */
  graph?: PipelineTemplate;
  /** The director's plan and wait state. Absent on every run that isn't directed. */
  director?: DirectorState;
  music_media_id?: string | null;
  provider?: string;
  model?: string;
  cost?: Record<string, unknown>;
  cost_summary?: CostSummary | Record<string, unknown>;
  /**
   * The run's cast registry. Absent (never `[]`) when the run has no
   * characters — the backend omits it — which is what the cast panel hides on.
   */
  cast?: CastMember[];
  /**
   * Set only on a run started for a series episode. `episode_id` is what the
   * Studio writes the assembled clip back onto — a run that carries neither is
   * an ordinary studio run and nothing series-shaped may be inferred from it.
   */
  series_id?: string;
  episode_id?: string;
  /**
   * The locked generation settings this run started with (Go
   * PipelineRun.GenerationSettings): the user's image and video models, cap and
   * image-generation count, snapshotted at start for a template marked
   * `generation_settings: "user"`. The run and its children keep it whatever the
   * user saves later. Absent on every other run.
   */
  generation_settings?: GenerationSettings;
  /**
   * The clip the completion hook built from the run's final output_contract
   * manifest (Go PipelineRun.ManifestClipID). Written AFTER PipelineCompleted,
   * so a just-finished run may not carry it yet.
   */
  manifest_clip_id?: string;
  /**
   * Set once Generate Clip retired the run. The list endpoint never returns an
   * archived run; GET /pipelines/:id still does, so its evidence stays
   * exportable after the thread leaves the rail.
   */
  archived_at?: string;
  created_at: string;
  updated_at: string;
}

// ==================== Pipeline Template Types ====================

export interface InputMapping {
  [placeholder: string]: string;
}

export interface PromptCheckpointConfig {
  provider?: string;
  model?: string;
}

export interface DistributorConfig {
  provider?: string;
  model?: string;
  max_children: number;
  /** Run parameter (e.g. "scene_count") that overrides max_children when set on a run. */
  max_children_param?: string;
  prompt_decorator?: string;
  /**
   * `checkpoint:<id>.<field>`: fan out over the elements of an earlier step's
   * `shots` array, one child per shot, with no planner call (and so no
   * `prompt_template_id`). Absent = the planner distributor, as before.
   */
  source?: string;
}

/**
 * Sequential distributor ("sequencer"): the planner decides the step count at
 * run time and the steps execute one after another, each able to reference
 * strictly-earlier steps. Mirrors Go pipeline.SequencerConfig.
 */
export interface SequencerConfig {
  provider?: string;
  model?: string;
  /** Hard cap on the number of steps. */
  max_steps?: number;
  /** Run parameter that lowers max_steps for a single run (e.g. "step_count"). */
  max_steps_param?: string;
  /** Named parent references a step may cite, e.g. { seed: "checkpoint:upload-1" }. */
  allowed_parent_refs?: Record<string, string>;
  /**
   * Who/what each declared ref is in THIS story, same keys as
   * allowed_parent_refs — shown to the reviewing judge on the reference's
   * label ("villain_ref": "the wooden forest creature the video follows").
   */
  ref_descriptions?: Record<string, string>;
}

export type ConnectorStrategy = 'collect_all';

export interface ConnectorConfig {
  strategy: ConnectorStrategy;
  source_checkpoint_id?: string;
}

export interface GeneratorConfig {
  modeLabel?: string;
  modes?: { id: string; label: string; type: string; options: string[] }[];
  media_type: string;
  role?: string;
  mode?: 'text_to_image' | 'image_to_image' | string;
  provider?: string;
  model?: string;
  /** Lets this generator produce a SECOND image for the same scene — the last
   *  frame of that scene's video clip — alongside its normal output. It grants
   *  the capability only; the planner decides scene by scene by emitting a
   *  `closing_frame` object on that scene's item. Image generators only. */
  closing_frame?: boolean;
  /**
   * Video generators only: which of the user's locked video models this one
   * runs on, `frames` (first/last frame) or `references` (reference images).
   * Two generators carry one each and a `skip_when` on the shot's mode picks
   * between them. Absent = the generator's own provider and model.
   */
  video_mode?: 'frames' | 'references' | string;
}

export interface UploadCheckpointConfig {
  media_type?: string;
  role?: string;
  required_count?: number; // default 1 when absent
  /**
   * The STORY name of the character this checkpoint collects pictures of. The
   * engine stamps it onto every accepted picture as `metadata.cast_name`, which
   * is what merges the upload onto the cast member the planner named instead of
   * creating a second, file-named one. Max 80 characters; one upload checkpoint
   * per named character.
   */
  cast_name?: string;
}

export interface CheckpointRequiredAsset {
  key?: string;
  type?: string;
  source?: string;
  checkpoint_id?: string;
  media_id?: string;
}

export interface ChainConnection {
  from_scene_id: string;
  to_scene_id: string;
}

/** provider/model pair, as the engine's pipeline.ModelRef. */
export interface ModelRef {
  provider?: string;
  model?: string;
}

/** One fixed check the judge must verify verbatim, alongside any inferred
 *  criteria. Mirrors the engine's `types.ReviewCheckSpec`. */
export interface ReviewCheckSpec {
  check: string;
  required?: boolean;
}

/**
 * The fixed lens vocabulary a judge can be pointed at — mirrors the engine's
 * `types.ReviewLenses()` (identity, count, wardrobe, setting, medium, text,
 * motion, continuity). Exported as a tuple so both the literal union type and
 * a runtime membership check (validatePipeline.ts) come from one place.
 */
export const REVIEW_LENSES = [
  'identity',
  'count',
  'wardrobe',
  'setting',
  'medium',
  'text',
  'motion',
  'continuity',
] as const;
export type ReviewLens = (typeof REVIEW_LENSES)[number];

/**
 * Optional AI review attached to a `prompt` or `generator` checkpoint. Absent
 * means the checkpoint behaves exactly as before — review is purely additive.
 * Models/thresholds left unset inherit the user's Reviewer settings.
 */
export interface ReviewConfig {
  judge_template_id?: string;
  /** input_mapping-style sources for the judge's variables; "criteria" required. */
  context?: Record<string, string>;
  check?: ModelRef;
  confirm?: ModelRef;
  /** 0 => the settings default (2). */
  max_attempts?: number;
  /** 0 => the settings default (0.85). */
  confidence_gate?: number;
  /** "" => "blocking". */
  severity_gate?: 'blocking' | 'any' | string;
  /** Prompt checkpoints only: context keys; when every one resolves empty
   *  ("", [], {}) the review is skipped — no judge call, no verdict, no cost. */
  skip_when_empty?: string[];

  // ---- capability fields (types.ReviewCapabilities, JSON-flattened) ----
  /** Give the judge the run's story/premise as extra context. */
  story?: boolean;
  /**
   * How many neighbouring scenes the judge also sees for continuity (0..3).
   * 1 and 2 are literal neighbour counts; 3 is "the story so far" — a
   * budgeted selection across the whole run, not three scenes.
   */
  scene_scope?: number;
  /** Give the judge the checkpoint's own text description alongside the media. */
  descriptions?: boolean;
  /** Give the judge prior failed attempts on this checkpoint, this run. */
  attempt_history?: boolean;
  /** Show the judge the run's cast — every character's reference picture and
   *  anchors — and have it rule on whether the candidate keeps their identity. */
  cast?: boolean;
  /** Run a second judge model and require both to agree. Only meaningful on a
   *  generator checkpoint with `generator.closing_frame: true` — see
   *  `validateReviewBlock` in validatePipeline.ts, which warns otherwise. */
  pair_judge?: boolean;
  /** The second judge model for `pair_judge`. Unset halves fall back to the
   *  resolved `confirm` model. */
  pair?: ModelRef;
  /** Checks the judge must verify verbatim, beyond any inferred criteria. */
  fixed_checks?: ReviewCheckSpec[];
  /**
   * Named lenses the judge should apply. Typed as the known union OR a bare
   * string array (rather than `ReviewLens[]` alone) so a value read back from
   * an unvalidated source — a draft template, an older account record — still
   * type-checks; `validateReviewBlock` is what actually enforces membership
   * in `REVIEW_LENSES` at save time.
   */
  lenses?: ReviewLens[] | string[];
  /** Free-text guidance appended to the judge prompt (<= 2000 chars). */
  guidance?: string;
}

/** The ten review-capability fields, shared verbatim between a checkpoint's
 *  `review` and a template's `review_defaults` — mirrors the Go engine
 *  embedding one `types.ReviewCapabilities` block in both cascade layers. */
export type ReviewCapabilityFields = Pick<
  ReviewConfig,
  | 'story'
  | 'scene_scope'
  | 'descriptions'
  | 'attempt_history'
  | 'cast'
  | 'pair_judge'
  | 'pair'
  | 'fixed_checks'
  | 'lenses'
  | 'guidance'
>;

/**
 * What a pipeline says the reviewer may DO, over the account's permissions.
 *
 * Tri-state, unlike the account's own block: an absent key inherits, and so
 * does an explicit `null` — the engine reads a pointer, and a template that
 * says nothing must not be read as saying "off".
 */
export interface ReviewDefaultsPermissions {
  /** The reviewer may regenerate an EARLIER scene that contradicts a frame. */
  cross_scene_regenerate?: boolean | null;
  /** How many such fixes one run may spend. */
  cross_scene_max_per_run?: number | null;
}

/**
 * Template-level review defaults (`PipelineTemplate.review_defaults`), applied
 * under the account's Reviewer settings and over by any checkpoint-level
 * `review` override. Mirrors the engine's `types.ReviewDefaults` — note it has
 * no `judge_template_id`/`context`; those are checkpoint-only.
 */
export interface ReviewDefaults extends ReviewCapabilityFields {
  mode?: string;
  check?: ModelRef;
  confirm?: ModelRef;
  max_attempts?: number;
  confidence_gate?: number;
  severity_gate?: 'blocking' | 'any' | string;
  permissions?: ReviewDefaultsPermissions;
}

/**
 * Turns a prompt checkpoint into a staged conversation that supplies one of its
 * mapped inputs. Absent on every checkpoint that has none.
 */
export interface DiscussionConfig {
  /** Which input_mapping key the conversation supplies. */
  input: string;
  system_template_id?: string;
  option_count?: number;
  max_turns?: number;
  provider?: string;
  model?: string;
}

/** Mirrors Go types.ToolCheckpointConfig (types/tool.go). */
export interface ToolCheckpointConfig {
  /** Namespaced sourcing tool wire name, e.g. "x.capture". */
  name: string;
  /** Bounds submit + polling; absent means the engine default (300 s). */
  timeout_s?: number;
}

/** Mirrors Go types.AgentLimits: zero/absent fields take the engine defaults. */
export interface AgentLimits {
  max_turns?: number;
  max_cost_usd?: number;
  /**
   * Caps the agent's own model calls only (not the paid tools it orders); 0 or
   * absent = no model-only cap. Reaching it pauses the run with code 905, and
   * Continue after raising it resumes without paying for the turns again.
   * `max_cost_usd` keeps its meaning (the total).
   */
  max_model_cost_usd?: number;
  per_tool?: Record<string, number>;
  timeout_s?: number;
}

/** Mirrors Go types.AgentCheckpointConfig ("agent" checkpoints only). */
export interface AgentCheckpointConfig {
  /** The agent's instructions (a prompt template). One of this and `objective` is set, never both. */
  prompt_template_id?: string;
  /** The agent's inline instructions, for an agent that has no saved prompt template. */
  objective?: string;
  /** Allowlist: engine tools (run.media, run.read, media.view, image.generate), sourcing tools, finish. */
  tools: string[];
  limits?: AgentLimits;
  /** A shipped agent output contract, e.g. "cover_choice@1"; empty when output_schema is set. */
  output_contract: string;
  /**
   * The agent's own result shape: an inline JSON Schema (Go
   * agentcontracts.ValidateInline). Exactly one of output_contract and
   * output_schema is set.
   */
  output_schema?: Record<string, unknown>;
  /** Optional model override; empty = the default agent model. */
  model?: { provider?: string; model?: string };
  /** Optional image generator for image.generate; absent = the pipeline's output_format image model. */
  image?: AgentImageChoice;
  /**
   * Checks the agent's finish as a list of clips: `script` names the input
   * source of the script shots the clips must cover, in order (Go
   * types.ShotListCheck).
   */
  shot_list?: { script?: string };
}

/**
 * Mirrors Go types.AgentImageChoice: provider and model go together (both, or
 * neither for the output_format default); settings is a free output_spec map
 * layered over output_format.image_settings and the checkpoint's output_spec.
 */
export interface AgentImageChoice {
  provider?: string;
  model?: string;
  settings?: Record<string, unknown>;
}

export interface CheckpointConfig {
  discussion?: DiscussionConfig;
  id: string;
  name: string;
  type?: CheckpointType;
  prompt_template_id: string;
  input_mapping: InputMapping;
  requires_confirm: boolean;

  output_spec?: Partial<MediaOutputSpec>;
  promptGate?: PromptCheckpointConfig;
  distributor?: DistributorConfig;
  sequencer?: SequencerConfig;
  connector?: ConnectorConfig;
  generator?: GeneratorConfig;
  upload?: UploadCheckpointConfig;
  /** "tool" checkpoints only. */
  tool?: ToolCheckpointConfig;
  /** "agent" checkpoints only. */
  agent?: AgentCheckpointConfig;
  /** "<clipstyle>@<version>", prompt checkpoints only: the engine validates the
   *  reply against that clip style's contract, and a run ending here becomes a
   *  clip of that style. */
  output_contract?: string;
  /** "<style>@<version>": the style that renders output_contract's data (Phase 4). Empty = the contract's own style. */
  render_style?: string;
  /** Prompt checkpoints only (Go CheckpointConfig.OutputFields): the engine
   *  checks the reply where it is made — one JSON object, required fields
   *  present, every listed field a string matching its pattern and format —
   *  and stores a missing optional field as "". */
  output_fields?: Record<string, OutputField>;
  /** AI review of this checkpoint's output (absent = no review). */
  review?: ReviewConfig;
  required_assets?: CheckpointRequiredAsset[];
  /** required_assets for the CLOSING frame, so the two frames of one scene can
   *  be wired to different references. Omitted = the opening frame alone
   *  (source "self:opening_frame"); declared = exactly that list, in order. */
  closing_assets?: CheckpointRequiredAsset[];
  chain_last_frames?: boolean;
  chain_connections?: ChainConnection[];
  skip_when?: SkipCondition;
  /** Hold this gate for the user even in an auto-mode run (a series episode):
   *  `requires_confirm` alone is passed in auto mode. Needs `requires_confirm`. */
  hold_in_auto_mode?: boolean;
  /** Whether this checkpoint's output lands on the render canvas. Undefined
   *  (= true, canvas-bound) keeps existing templates unchanged; false exempts
   *  intermediate/reference assets from the clipstyle ratio check. */
  canvas_bound?: boolean;
}

/** One field of a prompt checkpoint's JSON reply (Go OutputField). `type` is
 *  "string" (the default) or "string_list" (a JSON list of strings, [] when
 *  missing); `pattern` is an RE2 expression the whole value (every item of a
 *  list) must match; `format` "emoji" takes "" or exactly one emoji, strings
 *  only. */
export interface OutputField {
  required?: boolean;
  type?: string;
  pattern?: string;
  format?: string;
}

/** Mirrors Go pipeline.SkipCondition: skip the checkpoint when the named run
 * parameter (`param`), or a field of an EARLIER checkpoint's output
 * (`checkpoint`, "<id>" or "<id>.<field>", resolved like an input_mapping
 * `checkpoint:<id>.<field>` source), resolves to the given value. One form per
 * rule. */
export interface SkipCondition {
  param?: string;
  checkpoint?: string;
  equals: string;
}

export interface PipelineOutputFormat {
  image_provider?: string;
  image_model?: string;
  video_provider?: string;
  video_model?: string;
  audio_provider?: string;
  audio_model?: string;
  image_settings?: Partial<MediaOutputSpec>;
  video_settings?: Partial<MediaOutputSpec>;
  audio_settings?: Partial<MediaOutputSpec>;
}

export interface PipelineParameterSpec {
  default?: string;
  description?: string;
  /**
   * Closed value set for this parameter. When present the run-start panel
   * renders a dropdown (the default preselected) instead of a free-text field.
   */
  options?: string[];
}

export interface PipelineTemplate {
  id: string;
  name: string;
  description: string;
  output_format?: PipelineOutputFormat;
  /** Run-level parameter declarations (e.g. scene_count) with string defaults. */
  parameters?: Record<string, PipelineParameterSpec>;
  checkpoints: CheckpointConfig[];
  version: number;
  user_id?: string;
  visibility?: 'private' | 'assigned' | 'global' | string;
  source?: 'local' | 'db' | string;
  assigned_user_ids?: string[];
  tags?: string[];
  created_at: string;
  updated_at: string;
  /** Review capability/model defaults applied to every reviewed checkpoint in
   *  this template, under the account's Reviewer settings and over by any
   *  checkpoint-level `review` override. Opaque passthrough at the boundary —
   *  see `normalizePipelineTemplate` — validated by `validateReviewBlock`. */
  review_defaults?: ReviewDefaults;
  /**
   * `"user"` marks a template whose runs use the user's locked generation
   * settings (the crew); any other value means unmarked. Carried as authored.
   */
  generation_settings?: string;
  /**
   * Opaque, editor-owned presentation data (e.g. node-canvas layout
   * positions). The backend never validates its contents — it is a pure
   * passthrough blob that must survive every save/load/clone/export/import
   * path. Tolerant typing: `layout` is the one shape we know about today,
   * but anything else in here rides along untouched.
   */
  ui?: { layout?: Record<string, { x: number; y: number }> } & Record<string, unknown>;
}

// ==================== Prompt Template Types ====================

export interface PromptTemplate {
  id: string;
  name: string;
  description: string;
  content: string;
  version: number;
  user_id?: string;
  created_at: string;
  updated_at: string;
}

// ==================== WebSocket Event Types ====================

export type PipelineEventType =
  | 'PipelineStarted'
  | 'CheckpointStarted'
  | 'CheckpointCompleted'
  | 'CheckpointFailed'
  | 'PipelinePaused'
  | 'PipelineCompleted'
  | 'PipelineCancelled'
  | 'DistributorCompleted'
  | 'ReviewVerdict'
  | 'ReviewTrace'
  | 'AgentProgress';

export interface DistributorCompletedEvent {
  event: 'DistributorCompleted';
  data: {
    run_id: string;
    child_count: number;
    outputs: string[];
  };
}

export interface ReviewVerdictEvent {
  event: 'ReviewVerdict';
  data: {
    run_id: string;
    checkpoint_id: string;
    checkpoint_index: number;
    verdict: ReviewVerdict;
  };
}

/** Fired alongside ReviewVerdict for every raw reviewer call, pass or fail. */
export interface ReviewTraceEvent {
  event: 'ReviewTrace';
  data: {
    run_id: string;
    checkpoint_id: string;
    checkpoint_index: number;
    trace: ReviewTrace;
  };
}

/** One agent step (turn, tool call, cost, finish); the run itself carries the log. */
export interface AgentProgressEvent {
  event: 'AgentProgress';
  data: {
    run_id: string;
    checkpoint_id: string;
    checkpoint_index: number;
    type: string;
    turn: number;
    tool?: string;
    text?: string;
    /** Media ids a tool-result event showed the model this turn (agentruntime.Event.Images,
     *  via Go agent_host.go Emit's "images" field). Not yet read by any handler — documented
     *  for wire-accuracy ahead of live per-tool-call rendering. */
    images?: string[];
    /** Media ids a tool-result event created or picked (agentruntime.Event.Media, Emit's
     *  "media" field). Same status as images above. */
    media?: string[];
    cost_usd?: number;
    total_usd?: number;
    error?: boolean;
    /** Only on type "saved" (Go agent_host.go SaveState): the run's status
     *  at that save (running, finished, failed, exhausted, stopped, cancelled). */
    status?: string;
  };
}

export interface PipelineStartedEvent {
  event: 'PipelineStarted';
  data: PipelineRun;
}

export interface CheckpointStartedEvent {
  event: 'CheckpointStarted';
  data: {
    run_id: string;
    checkpoint_index: number;
    checkpoint_id: string;
    checkpoint_name: string;
    checkpoint_type?: CheckpointType;
  };
}

export interface CheckpointCompletedEvent {
  event: 'CheckpointCompleted';
  data: {
    run_id: string;
    checkpoint_index: number;
    checkpoint_id: string;
    checkpoint_name: string;
    output: string;
  };
}

export interface CheckpointFailedEvent {
  event: 'CheckpointFailed';
  data: {
    run_id: string;
    checkpoint_index: number;
    error: string;
    error_code?: number;
    /** Present for a distributor child run: the parent run the failure belongs to. */
    parent_run_id?: string;
    /** Present for a distributor child run: the failed child run's own id. */
    child_run_id?: string;
  };
}

export interface PipelinePausedEvent {
  event: 'PipelinePaused';
  data: {
    run_id: string;
    checkpoint_index: number;
    checkpoint_name: string;
    awaiting_confirm: boolean;
  };
}

export interface PipelineCompletedEvent {
  event: 'PipelineCompleted';
  data: {
    run_id: string;
    final_output: string;
  };
}

export interface PipelineCancelledEvent {
  event: 'PipelineCancelled';
  data: {
    run_id: string;
  };
}

export type PipelineEvent =
  | PipelineStartedEvent
  | CheckpointStartedEvent
  | CheckpointCompletedEvent
  | CheckpointFailedEvent
  | PipelinePausedEvent
  | PipelineCompletedEvent
  | PipelineCancelledEvent
  | DistributorCompletedEvent
  | ReviewVerdictEvent
  | ReviewTraceEvent
  | AgentProgressEvent;
